/**
 * Public links that sell ONE product on its own — the single-product page, no
 * sales page needed. A storefront host serves it at /product/<slug>; a store
 * with neither a subdomain nor a verified domain only has the apex
 * /shop/<store_slug>/<slug> form.
 */

/** Query flag that opens the product page straight on the payment form. */
export const DIRECT_CHECKOUT_PARAM = "checkout";

export interface ProductLinkInput {
  /** storeOrigin(): verified custom domain, else the platform subdomain. */
  storeOrigin: string | null;
  /** Apex app URL, used only with storeSlug. */
  appUrl: string;
  /** user_settings.store_slug — the apex /shop handle. */
  storeSlug: string | null;
  productSlug: string;
}

export interface ProductLinks {
  page: string;
  checkout: string;
}

export function productLinks(input: ProductLinkInput): ProductLinks | null {
  const slug = encodeURIComponent(input.productSlug.trim());
  if (!slug) return null;
  let page: string;
  if (input.storeOrigin) {
    page = `${trimSlash(input.storeOrigin)}/product/${slug}`;
  } else if (input.storeSlug?.trim()) {
    page = `${trimSlash(input.appUrl)}/shop/${encodeURIComponent(input.storeSlug.trim())}/${slug}`;
  } else {
    return null;
  }
  return { page, checkout: `${page}?${DIRECT_CHECKOUT_PARAM}=1` };
}

/** True when a product-page URL asked to open on the payment form. */
export function wantsDirectCheckout(search: string): boolean {
  const v = new URLSearchParams(search).get(DIRECT_CHECKOUT_PARAM);
  return v === "1" || v === "true";
}

/** Query flag that opens the product page on its linked sales page (an Iklan HQ ad's "Sales page"). */
export const SALES_PAGE_PARAM = "sp";

/** True when a product-page URL asked to open on its sales page. */
export function wantsSalesPage(search: string): boolean {
  const v = new URLSearchParams(search).get(SALES_PAGE_PARAM);
  return v === "1" || v === "true";
}

function trimSlash(s: string): string {
  return s.replace(/\/+$/, "");
}

/** Window the product list's link column reports on. */
export const LINK_STATS_DAYS = 30;

export interface ProductLinkStats {
  views: number;
  visitors: number;
  orders: number;
  revenue: number;
  /** Paid orders per visitor, in percent to one decimal; null with no visitors. */
  conversion: number | null;
}

/** What every product's own link did over the last `days`, keyed by product id. */
export interface StoreLinkStats {
  days: number;
  products: Record<string, ProductLinkStats>;
}

/** Every path a product's own page is served on: /product/<slug> on the store's
 *  host, /shop/<store_slug>/<slug> on the apex. */
export function productPagePaths(productSlug: string, storeSlug: string | null): string[] {
  const paths = [`/product/${productSlug}`];
  if (storeSlug) paths.push(`/shop/${storeSlug}/${productSlug}`);
  return paths;
}

export function conversionRate(orders: number, visitors: number): number | null {
  if (visitors <= 0) return null;
  return Math.round((orders / visitors) * 1000) / 10;
}

/**
 * Fold page-less view rows and product-link orders into per-product numbers.
 * A visitor who reached the same product on both its storefront and apex path
 * counts once. Rows for paths that are no product's page (a renamed slug, a
 * deleted product) are dropped.
 */
export function aggregateLinkStats(input: {
  products: { id: string; slug: string }[];
  storeSlug: string | null;
  /** One row per (path, visitor) with that pair's view count. */
  views: { path: string | null; visitor_id: string; n: number }[];
  orders: { product_id: string | null; amount: number | string }[];
}): Record<string, ProductLinkStats> {
  const byPath = new Map<string, string>();
  for (const p of input.products) {
    for (const path of productPagePaths(p.slug, input.storeSlug)) byPath.set(path, p.id);
  }
  const views = new Map<string, number>();
  const visitors = new Map<string, Set<string>>();
  for (const v of input.views) {
    const id = v.path ? byPath.get(v.path) : undefined;
    if (!id) continue;
    views.set(id, (views.get(id) ?? 0) + Number(v.n));
    let set = visitors.get(id);
    if (!set) visitors.set(id, (set = new Set()));
    set.add(v.visitor_id);
  }
  const orders = new Map<string, { n: number; cents: number }>();
  for (const o of input.orders) {
    if (!o.product_id) continue;
    const agg = orders.get(o.product_id) ?? { n: 0, cents: 0 };
    agg.n += 1;
    agg.cents += Math.round(Number(o.amount) * 100);
    orders.set(o.product_id, agg);
  }
  const out: Record<string, ProductLinkStats> = {};
  for (const p of input.products) {
    const seen = visitors.get(p.id)?.size ?? 0;
    const sold = orders.get(p.id) ?? { n: 0, cents: 0 };
    out[p.id] = {
      views: views.get(p.id) ?? 0,
      visitors: seen,
      orders: sold.n,
      revenue: sold.cents / 100,
      conversion: conversionRate(sold.n, seen),
    };
  }
  return out;
}
